// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import type { InstallCloudProvider } from '@greenlight/shared';

/** The managed Kubernetes service each cloud runs the install on, and where it lives. */
const CLUSTER_KIND: Record<
  InstallCloudProvider,
  { article: 'a' | 'an'; service: string; short: string; home: string }
> = {
  azure: {
    article: 'an',
    service: 'Azure Kubernetes Service',
    short: 'AKS',
    home: 'your Azure subscription',
  },
  gcp: {
    article: 'a',
    service: 'Google Kubernetes Engine',
    short: 'GKE',
    home: 'your Google Cloud project',
  },
  aws: {
    article: 'an',
    service: 'Amazon Elastic Kubernetes Service',
    short: 'EKS',
    home: 'your AWS account',
  },
};

/**
 * Groups the cards that speak Kubernetes, and says once that it is Kubernetes:
 * the managed service by name, and what a node and a pod are. Each card inside
 * says in plain words what its own figures are.
 */
export function KubernetesClusterSection({
  cloud,
  children,
}: {
  readonly cloud: InstallCloudProvider | null | undefined;
  readonly children: React.ReactNode;
}): React.ReactElement {
  const kind = cloud ? CLUSTER_KIND[cloud] : null;
  return (
    <section
      className="space-y-4 rounded-gl-lg border border-border bg-muted/40 p-4 md:col-span-2 md:p-5"
      aria-labelledby="kubernetes-cluster-heading"
      data-testid="kubernetes-cluster"
    >
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <h2 id="kubernetes-cluster-heading" className="text-sm font-semibold text-foreground">
            Kubernetes cluster health
          </h2>
          {kind ? (
            <span className="rounded-md border border-border bg-card px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
              {kind.short}
            </span>
          ) : null}
        </div>
        <p className="max-w-[92ch] text-xs leading-relaxed text-muted-foreground">
          Greenlight and your apps run on{' '}
          {kind ? (
            <>
              {kind.article} {kind.service} ({kind.short}) cluster in {kind.home}
            </>
          ) : (
            'a Kubernetes cluster in your cloud account'
          )}
          , as <span className="text-foreground">pods</span> (containers) on its{' '}
          <span className="text-foreground">nodes</span> (virtual machines).
        </p>
      </div>
      {children}
    </section>
  );
}
